/**
 * 语言检测与持久化。
 *
 * 多语言独立 URL 架构（2026-10）下语言由 URL 前缀唯一决定（见 languageFromPath）：
 * 无前缀 = zh-CN，/en|/ko|/ru 前缀 = 对应语言。localStorage/navigator 检测**不再参与**
 * 初始语言（否则 /home/xxx 对不同访客返回不同语言内容，爬虫会收录错语言），
 * 仅保留 detectLanguage 供测试与潜在的场景化使用；persistLanguage 仍是切换器的偏好记忆。
 * 存储值为 JSON 字符串形态（如 '"en"'），与 utils/browser.ts 的 getLocalStorageItem
 * 的 JSON.parse 语义对齐——直接存裸 'en' 会在读取时抛错，勿改。
 *
 * ko/ru 已于 2026-09-17 补齐全部 8 个命名空间词典（见 resources.ts），
 * 页面残留的少量中文为刻意保留：品牌名、公司备案/版权（法律原文）、语言母语自称、第三方产品名。
 */
export type AppLanguage = 'en' | 'zh-CN' | 'ko' | 'ru';

export const SUPPORTED_LANGUAGES: AppLanguage[] = ['zh-CN', 'en', 'ko', 'ru'];

/** URL 前缀 → AppLanguage（中文为默认无前缀路径，不在此表中） */
const PATH_LANGUAGES: Record<string, AppLanguage> = {en: 'en', ko: 'ko', ru: 'ru'};

/**
 * 从 pathname 解析语言前缀：跳过第一段（站点子路径 /home 或 /home-test），看第二段。
 * 如 /home/en/image-translator → 'en'；/home/image-translator → null（默认中文）。
 * 站内不存在名为 en/ko/ru 的一级路由，无冲突。
 */
export const languageFromPath = (pathname: string): AppLanguage | null => {
  const segments = pathname.split('/').filter(Boolean);
  const second = segments[1];
  return (second && PATH_LANGUAGES[second]) || null;
};

const LANG_STORAGE_KEY = 'XJ_LANG';

const toAppLanguage = (tag: string): AppLanguage => {
  const lower = tag.toLowerCase();
  if (lower.startsWith('zh')) return 'zh-CN';
  if (lower.startsWith('ko')) return 'ko';
  if (lower.startsWith('ru')) return 'ru';
  return 'en';
};

export const detectLanguage = (): AppLanguage => {
  try {
    const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed === 'en' || parsed === 'zh-CN' || parsed === 'ko' || parsed === 'ru') return parsed;
    }
  } catch {
    // localStorage 不可用或脏数据，继续探测系统语言
  }
  const navLanguage = typeof navigator !== 'undefined' ? navigator.language || '' : '';
  return navLanguage ? toAppLanguage(navLanguage) : 'zh-CN';
};

export const persistLanguage = (language: AppLanguage) => {
  try {
    window.localStorage.setItem(LANG_STORAGE_KEY, JSON.stringify(language));
  } catch {
    // 隐私模式等写入失败可忽略
  }
};
